import { LegalTypeEnum } from '@@fire-b2b/types/legal/LegalTypeEnum'
import type { OrderTypeEnum } from '@@fire-b2b/types/order/OrderTypeEnum'

export function useRoutePath() {
  const localePath = useLocalePath()

  const createPath = (route: Parameters<typeof localePath>[0], locale?: Parameters<typeof localePath>[1]): string => {
    const resolved = localePath(route, locale)
    if (!resolved) {
      console.warn(`The route for "${JSON.stringify(route)}" could not be resolved.`)
    }
    return resolved
  }

  return {
    register: () => {
      return createPath({
        name: 'register',
      })
    },

    login: () => {
      return createPath({
        name: 'login',
      })
    },

    ssoEntry: () => {
      return createPath({
        name: 'sso-entry',
      })
    },

    newPassword: () => {
      return createPath({
        name: 'new-password',
      })
    },

    passwordReset: () => {
      return createPath({
        name: 'password-reset',
      })
    },

    landing: (params?: { brandId?: number; orderType?: OrderTypeEnum }) => {
      return createPath({
        name: 'landing',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          orderType: params?.orderType,
        },
      })
    },

    accountProfile: () => {
      return createPath({
        name: 'account-profile',
      })
    },

    accountAddresses: () => {
      return createPath({
        name: 'account-addresses',
      })
    },

    accountOrderHistory: () => {
      return createPath({
        name: 'account-order-history',
      })
    },

    accountOrderHistoryDetail: (params: { id: number }) => {
      return createPath({
        name: 'account-order-history-detail',
        params: {
          id: params.id,
        },
      })
    },

    accountContracts: () => {
      return createPath({
        name: 'account-contracts',
      })
    },

    accountCompany: () => {
      return createPath({
        name: 'account-company',
      })
    },

    accountDocuments: () => {
      return createPath({
        name: 'account-documents',
      })
    },

    accountUsers: () => {
      return createPath({
        name: 'account-users',
      })
    },

    accountNotifications: () => {
      return createPath({
        name: 'account-notifications',
      })
    },

    accountOpenOrders: () => {
      return createPath({
        name: 'account-open-orders',
      })
    },

    accountWishlists: () => {
      return createPath({
        name: 'account-wishlists',
      })
    },

    teamCategory: (params?: { brandId?: number; id?: number; slug?: string }) => {
      return createPath({
        name: 'team-category',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          teamCategoryIdAndSlug: combineOptionalIdAndSlug(params?.id, params?.slug),
        },
      })
    },

    supportCenterCategory: (params: { brandId?: number; slug: string; id: number }) => {
      return createPath({
        name: 'support-center-category',
        params: {
          brandIdAndSlug: combineIdAndSlug(params.brandId),
          supportCategoryIdAndSlug: combineIdAndSlug(params.id, params.slug),
        },
      })
    },

    supportCenterDetail: (params: { brandId?: number; slug: string; id: number }) => {
      return createPath({
        name: 'support-center-detail',
        params: {
          brandIdAndSlug: combineIdAndSlug(params.brandId),
          supportDetailIdAndSlug: combineIdAndSlug(params.id, params.slug),
        },
      })
    },

    blogCategory: (params?: { brandId?: number; id?: number; slug?: string }) => {
      return createPath({
        name: 'blog-category',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          blogCategoryIdAndSlug: combineOptionalIdAndSlug(params?.id, params?.slug),
        },
      })
    },

    blogDetail: (params: {
      brandId?: number
      categoryId?: number
      categorySlug?: string
      id: number
      slug?: string
    }) => {
      return createPath({
        name: 'blog-detail',
        params: {
          brandIdAndSlug: combineIdAndSlug(params.brandId),
          blogCategoryIdAndSlug: combineOptionalIdAndSlug(params.categoryId, params.categorySlug),
          blogDetailIdAndSlug: combineIdAndSlug(params.id, params.slug),
        },
      })
    },

    formsDetail: (params: { brandId?: number; formIdent: string }) => {
      return localePath({
        name: 'forms-detail',
        params: {
          brandIdAndSlug: combineIdAndSlug(params.brandId),
          ident: params.formIdent,
        },
      })
    },

    formsConfirmation: () => {
      return localePath({
        name: 'forms-confirmation',
      })
    },

    imprint: () => {
      return localePath({
        name: 'legal-page',
        params: {
          legalIdent: LegalTypeEnum.IMPRINT,
        },
      })
    },

    termsOfService: () => {
      return localePath({
        name: 'legal-page',
        params: {
          legalIdent: LegalTypeEnum.TERMS_OF_SERVICE,
        },
      })
    },

    privacyPolicy: () => {
      return localePath({
        name: 'legal-page',
        params: {
          legalIdent: LegalTypeEnum.PRIVACY_POLICY,
        },
      })
    },

    downloadCategory: (params?: {
      brandId?: number
      categoryId?: number
      categorySlug?: string
      downloadId?: number
      downloadSlug?: string
    }) => {
      return createPath({
        name: 'download-category',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          categoryIdAndSlug: combineOptionalIdAndSlug(params?.categoryId, params?.categorySlug),
          downloadIdAndSlug: combineOptionalIdAndSlug(params?.downloadId, params?.downloadSlug),
        },
      })
    },

    catalog: (params?: {
      brandId?: number
      orderType?: OrderTypeEnum
      seasonId?: number
      productLineId?: number
      shopRootCategoryId?: number
      shopRootCategorySlug?: string
      shopCategoryId?: number
      shopCategorySlug?: string
    }) => {
      return createPath({
        name: 'catalog',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          orderType: params?.orderType,
          seasonIdAndSlug: combineOptionalIdAndSlug(params?.seasonId),
          productlineIdAndSlug: combineOptionalIdAndSlug(params?.productLineId),
          shopRootCategoryIdAndSlug: combineOptionalIdAndSlug(params?.shopRootCategoryId, params?.shopRootCategorySlug),
          shopCategoryIdAndSlug: combineOptionalIdAndSlug(params?.shopCategoryId, params?.shopCategorySlug),
        },
      })
    },

    search: (params: {
      brandId?: number
      orderType?: OrderTypeEnum
      seasonId?: number
      productLineId?: number
      shopCategoryId?: number
      shopCategorySlug?: string
      searchQuery?: string
    }) => {
      return createPath({
        name: 'search',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          orderType: params?.orderType,
          seasonIdAndSlug: combineOptionalIdAndSlug(params?.seasonId),
          productlineIdAndSlug: combineOptionalIdAndSlug(params?.productLineId),
          shopCategoryIdAndSlug: combineOptionalIdAndSlug(params?.shopCategoryId, params?.shopCategorySlug),
          searchQuery: params.searchQuery ?? OPTIONAL_PARAM_DEFAULT_VALUE,
        },
      })
    },

    productDetail: (params: { brandId?: number; orderType?: OrderTypeEnum; slug: string; id: number }) => {
      return createPath({
        name: 'product-detail',
        params: {
          brandIdAndSlug: combineIdAndSlug(params.brandId),
          orderType: params.orderType,
          productIdAndSlug: combineIdAndSlug(params.id, params.slug),
        },
      })
    },

    cart: (params?: { brandId?: number; orderType?: OrderTypeEnum }) => {
      return createPath({
        name: 'cart',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          orderType: params?.orderType,
        },
      })
    },

    basketCheckout: (params?: { brandId?: number; orderType?: OrderTypeEnum }) => {
      return createPath({
        name: 'basket-checkout',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          orderType: params?.orderType,
        },
      })
    },

    basketCheckoutConfirmation: (params?: { brandId?: number; orderType?: OrderTypeEnum }) => {
      return createPath({
        name: 'basket-checkout-confirmation',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          orderType: params?.orderType,
        },
      })
    },

    openOrders: (params?: { brandId?: number; orderType?: OrderTypeEnum }) => {
      return createPath({
        name: 'open-orders',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          orderType: params?.orderType,
        },
      })
    },

    openOrder: (params: { brandId?: number; orderType?: OrderTypeEnum; wishlistId: number }) => {
      return createPath({
        name: 'open-order-detail',
        params: {
          brandIdAndSlug: combineIdAndSlug(params.brandId),
          orderType: params.orderType,
          wishlistId: params.wishlistId,
        },
      })
    },

    openOrderCheckout: (params: { brandId?: number; orderType?: OrderTypeEnum; wishlistId: number }) => {
      return createPath({
        name: 'open-order-checkout',
        params: {
          brandIdAndSlug: combineIdAndSlug(params.brandId),
          orderType: params.orderType,
          wishlistId: params.wishlistId,
        },
      })
    },

    openOrderConfirmation: (params: { brandId?: number; orderType?: OrderTypeEnum; wishlistId: number }) => {
      return createPath({
        name: 'open-order-confirmation',
        params: {
          brandIdAndSlug: combineIdAndSlug(params.brandId),
          orderType: params.orderType,
          wishlistId: params.wishlistId,
        },
      })
    },

    submission: (params?: { brandId?: number; orderType?: OrderTypeEnum }) => {
      return createPath({
        name: 'submission',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          orderType: params?.orderType,
        },
      })
    },

    submissionDetail: (params: { brandId?: number; orderType?: OrderTypeEnum; id: number }) => {
      return createPath({
        name: 'submission-detail',
        params: {
          brandIdAndSlug: combineIdAndSlug(params.brandId),
          orderType: params.orderType,
          id: params.id,
        },
      })
    },

    capsuleCategory: (params?: {
      brandId?: number
      orderType?: OrderTypeEnum
      seasonId?: number
      productLineId?: number
      categoryId?: number
      categorySlug?: string
    }) => {
      return createPath({
        name: 'capsule-category',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          orderType: params?.orderType,
          seasonIdAndSlug: combineOptionalIdAndSlug(params?.seasonId),
          productlineIdAndSlug: combineOptionalIdAndSlug(params?.productLineId),
          themeworldCategoryIdAndSlug: combineOptionalIdAndSlug(params?.categoryId, params?.categorySlug),
        },
      })
    },

    capsuleDetail: (params: {
      brandId?: number
      orderType?: OrderTypeEnum
      themeworldId: number
      themeworldSlug: string
    }) => {
      return createPath({
        name: 'capsule-detail',
        params: {
          brandIdAndSlug: params.brandId,
          orderType: params.orderType,
          themeworldIdAndSlug: combineIdAndSlug(params.themeworldId, params.themeworldSlug),
        },
      })
    },

    lookCategory: (params?: {
      brandId?: number
      orderType?: OrderTypeEnum
      seasonId?: number
      productLineId?: number
      id?: number
      slug?: string
    }) => {
      return createPath({
        name: 'look-category',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          orderType: params?.orderType,
          seasonIdAndSlug: combineOptionalIdAndSlug(params?.seasonId),
          productlineIdAndSlug: combineOptionalIdAndSlug(params?.productLineId),
          lookCategoryIdAndSlug: combineOptionalIdAndSlug(params?.id, params?.slug),
        },
      })
    },

    lookDetail: (params: { brandId?: number; orderType?: OrderTypeEnum; slug: string; id: number }) => {
      return createPath({
        name: 'look-detail',
        params: {
          brandIdAndSlug: params.brandId,
          orderType: params.orderType,
          lookIdAndSlug: combineIdAndSlug(params.id, params.slug),
        },
      })
    },

    wishlist: (params?: { brandId?: number; orderType?: OrderTypeEnum }) => {
      return createPath({
        name: 'wishlist',
        params: {
          brandIdAndSlug: combineIdAndSlug(params?.brandId),
          orderType: params?.orderType,
        },
      })
    },

    wishlistDetail: (params: { brandId?: number; orderType?: OrderTypeEnum; wishlistId: number }) => {
      return createPath({
        name: 'wishlist-detail',
        params: {
          brandIdAndSlug: combineIdAndSlug(params.brandId),
          orderType: params.orderType,
          wishlistId: params.wishlistId,
        },
      })
    },

    resources: () => {
      return createPath({
        name: 'resources-root',
        params: {},
      })
    },
  }
}
export type UseRoutePath = ReturnType<typeof useRoutePath>

function combineOptionalIdAndSlug(id?: number, slug?: string): string {
  return combineIdAndSlug(id, slug) ?? OPTIONAL_PARAM_DEFAULT_VALUE
}

function combineIdAndSlug(id?: number, slug?: string): string | undefined {
  if (!id) {
    return undefined
  }

  return slug ? `${id}-${slug}` : `${id}`
}
export type RoutePath = ReturnType<typeof useRoutePath>
